import { CheckIcon } from "@heroicons/react/20/solid";
import { useLanguage } from "../../../contexts/LanguageContext";
import { WEBHOOK_EVENT_OPTIONS } from "../../../constants/webhookEvents";
import { Field, SecretInput, Section, inputClass } from "./ui";

// Authorization and event subscription for a webhook, in create and edit.
export default function WebhookSection({ values, setValue, setValues }) {
  const { t } = useLanguage();
  const events = values.webhookEvents;

  const toggleEvent = (value) =>
    setValue(
      "webhookEvents",
      events.includes(value) ? events.filter((ev) => ev !== value) : [...events, value],
    );

  return (
    <>
      <Section
        title={t("integrations.webhook.authorization")}
        description={t("integrations.webhook.authDescription")}
      >
        <Field label={t("integrations.webhook.authType")} htmlFor="integration-auth-type">
          <select
            id="integration-auth-type"
            value={values.webhookAuthType}
            onChange={(e) => {
              const authType = e.target.value;
              setValues((prev) => ({
                ...prev,
                webhookAuthType: authType,
                ...(authType === "none" && { webhookAuthToken: "" }),
              }));
            }}
            className={inputClass}
          >
            <option value="none">{t("integrations.webhook.authNone")}</option>
            <option value="bearer">{t("integrations.webhook.authBearer")}</option>
          </select>
        </Field>
        {values.webhookAuthType === "bearer" && (
          <Field
            label={t("integrations.webhook.bearerToken")}
            htmlFor="integration-auth-token"
            hint={t("integrations.webhook.bearerHint", {
              header: (
                <span className="font-mono">Authorization: Bearer &lt;token&gt;</span>
              ),
            })}
          >
            <SecretInput
              id="integration-auth-token"
              value={values.webhookAuthToken}
              onChange={(e) => setValue("webhookAuthToken", e.target.value)}
              placeholder="your-secret-token"
              required
            />
          </Field>
        )}
      </Section>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-[13px] font-semibold text-gray-700">
          {t("integrations.webhook.events")}
        </legend>
        <p className="-mt-1 mb-1 text-xs text-gray-600">
          {t("integrations.webhook.selectEvents")}
        </p>
        {WEBHOOK_EVENT_OPTIONS.map((option) => {
          const checked = events.includes(option.value);
          return (
            <label
              key={option.value}
              className="flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleEvent(option.value)}
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className={`mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-indigo-600 ${
                  checked ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-400 bg-white"
                }`}
              >
                {checked && <CheckIcon className="h-3.5 w-3.5" />}
              </span>
              <span className="flex flex-col gap-0.5">
                <span>{t(option.labelKey)}</span>
                <span className="font-mono text-xs text-gray-600">{option.value}</span>
              </span>
            </label>
          );
        })}
        {events.length === 0 && (
          <p role="alert" className="text-xs font-medium text-red-700">
            {t("integrations.webhook.selectAtLeastOne")}
          </p>
        )}
      </fieldset>
    </>
  );
}
